import {
    cancelSelectEvent,
    getCSSStyles,
    getEChartsTooltipTheme,
    getHeaderPinButtonHTML,
    getHeaderPinStateClass,
    getNoDataHTML,
    getWidgetDataURI,
    normalizeWidgetTheme,
    setupHeaderPin,
    toggleWidgetTheme
} from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

type AssetTradeDirection = 'purchase' | 'sale';

interface AssetPopularityItem {
    symbol: string;
    trades: number;
    amount: number;
    politicians: number;
    direction: AssetTradeDirection;
}

const PURCHASE_COLOR = '#7CBA05';
const SALE_COLOR = '#92090A';
const BUBBLE_SERIES_ID = 'congressAssetPopularityBubbles';
const HOVER_DOT_SERIES_ID = 'congressAssetPopularityHoverDot';

export class TraderverseCongressAllAssetsPopularity {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private chartData: AssetPopularityItem[] = [];
    private currentChart: any = null;
    private widgetResizeObserver: ResizeObserver | null = null;
    private resizeHandler: (() => void) | null = null;

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        return {
            headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
   <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Congress All Assets Popularity</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="chartDv" id="congressAllAssetsPopularity">
                <div class="assetPopularityLegend">
                    <span class="assetPopularityLegendDot sale" title="Sales"></span>
                    <span class="assetPopularityLegendDot purchase" title="Purchases"></span>
                </div>
                <div id="chartContainer" class="chart assetPopularityChart"></div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        setupHeaderPin(this.shadowRoot, this.config);
    }

    private getDummyData(): AssetPopularityItem[] {
        return [
            { symbol: 'NVDA', trades: 6, amount: 26, politicians: 18, direction: 'sale' },
            { symbol: 'AAPL', trades: 10, amount: 40, politicians: 24, direction: 'purchase' },
            { symbol: 'MSFT', trades: 14, amount: 52, politicians: 38, direction: 'sale' },
            { symbol: 'AMZN', trades: 17, amount: 41, politicians: 20, direction: 'purchase' },
            { symbol: 'META', trades: 23, amount: 39, politicians: 22, direction: 'purchase' },
            { symbol: 'TFL', trades: 52, amount: 125, politicians: 96, direction: 'purchase' },
            { symbol: 'GOOGL', trades: 68, amount: 30, politicians: 34, direction: 'sale' },
            { symbol: 'TSLA', trades: 12, amount: 36, politicians: 16, direction: 'purchase' }
        ];
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
            this.injectChartStyles();
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(): Promise<void> {
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const containerWrapper = this.shadowRoot.querySelector('#congressAllAssetsPopularity') as HTMLElement;
        if (widgetBox) widgetBox.classList.add('loading');

        this.chartData = this.getDummyData();
        containerWrapper?.setAttribute('data-testing-id', 'congress-all-assets-popularity');
        this.updateDisplay();
        this.emit('dataUpdate', { data: this.chartData });

        if (widgetBox) widgetBox.classList.remove('loading');
    }

    private updateDisplay(): void {
        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.drawChart();
    }

    private formatBillions(value: number): string {
        if (value === 0) return '0';
        const abs = Math.abs(value);
        if (abs >= 1000) {
            const trillions = value / 1000;
            return Number.isInteger(trillions) ? `${trillions}T` : `${trillions.toFixed(1)}T`;
        }
        return Number.isInteger(value) ? `${value}B` : `${value.toFixed(1)}B`;
    }

    private updateWidgetLayout(chartHeight: number): void {
        const legendHeight = 34;
        const chartDv = this.shadowRoot.querySelector('#congressAllAssetsPopularity') as HTMLElement | null;
        const inBody = this.shadowRoot.querySelector('.inBody') as HTMLElement | null;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;

        const chartDvHeight = legendHeight + chartHeight;
        if (chartDv) {
            chartDv.style.height = `${chartDvHeight}px`;
            chartDv.style.minHeight = `${chartDvHeight}px`;
            chartDv.style.flex = '0 0 auto';
        }

        if (this.config.autosize === false) return;

        const headTop = this.shadowRoot.querySelector('.headTop') as HTMLElement | null;
        const headTopHeight = headTop?.offsetHeight ?? 56;
        const inBodyPadding = 30;
        const inBodyHeight = chartDvHeight + inBodyPadding;

        if (inBody) {
            inBody.style.height = `${inBodyHeight}px`;
            inBody.style.minHeight = `${inBodyHeight}px`;
        }
        if (widgetBox) {
            widgetBox.style.height = `${headTopHeight + inBodyHeight}px`;
        }
    }

    private injectChartStyles(): void {
        const existing = this.shadowRoot.querySelector('style[data-congress-asset-popularity]');
        if (existing) existing.remove();

        const style = document.createElement('style');
        style.setAttribute('data-congress-asset-popularity', 'true');
        style.textContent = `
            #congressAllAssetsPopularity.chartDv {
                height: auto;
                min-height: 400px;
                display: flex;
                flex-direction: column;
                flex: 0 0 auto;
                overflow: visible;
            }
            .assetPopularityLegend {
                display: flex;
                align-items: center;
                padding: 2px 0 12px;
                flex: 0 0 auto;
            }
            .assetPopularityLegendDot {
                width: 18px;
                height: 18px;
                border-radius: 50%;
                display: inline-block;
            }
            .assetPopularityLegendDot.sale {
                background: ${SALE_COLOR};
            }
            .assetPopularityLegendDot.purchase {
                background: ${PURCHASE_COLOR};
                margin-left: -4px;
            }
            #congressAllAssetsPopularity .assetPopularityChart {
                position: relative !important;
                left: auto !important;
                top: auto !important;
                width: 100%;
                height: 400px;
                flex: 0 0 auto;
                min-height: 400px;
            }
            .widgetBox:has(#congressAllAssetsPopularity) .inBody {
                height: auto;
                min-height: 470px;
                overflow: visible;
            }
        `;
        this.shadowRoot.appendChild(style);
    }

    private escapeHtml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
        const containerWrapper = this.shadowRoot.querySelector('#congressAllAssetsPopularity') as HTMLElement;

        if (!container) return;

        if (this.currentChart) {
            try { this.currentChart.dispose(); } catch { /* ignore */ }
            this.currentChart = null;
        }
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler = null;
        }

        const chartData = [...this.chartData];
        if (!chartData.length) {
            container.innerHTML = getNoDataHTML();
            containerWrapper?.removeAttribute('data-testing-id');
            return;
        }

        container.innerHTML = '';
        containerWrapper?.setAttribute('data-testing-id', 'congress-all-assets-popularity');

        const scaleMaxTrades = 80;
        const scaleMaxAmount = 175;

        const politicianValues = chartData.map((item) => item.politicians);
        const minPoliticians = Math.min(...politicianValues);
        const maxPoliticians = Math.max(...politicianValues);

        const chartHeight = 400;
        container.style.height = `${chartHeight}px`;
        container.style.minHeight = `${chartHeight}px`;
        container.style.width = '100%';
        this.updateWidgetLayout(chartHeight);

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const widgetStyles = getComputedStyle(widgetBox);
        const textColor = widgetStyles.getPropertyValue('--charts-text-color').trim() || '#B8B3C9';
        const splitLineColor = widgetStyles.getPropertyValue('--charts-split-line-color').trim() || 'rgba(148, 163, 184, 0.25)';
        const fontSize = widgetStyles.getPropertyValue('--font-size-sm').trim() || '12px';
        const labelFontSize = parseInt(fontSize, 10) || 12;

        const scatterData = chartData.map((item) => {
            const sizeRatio = maxPoliticians === minPoliticians
                ? 0.5
                : (item.politicians - minPoliticians) / (maxPoliticians - minPoliticians);
            const symbolSize = 22 + sizeRatio * 50;
            const color = item.direction === 'purchase' ? PURCHASE_COLOR : SALE_COLOR;
            return {
                value: [item.trades, item.amount, item.politicians],
                assetSymbol: item.symbol,
                symbolSize,
                itemStyle: { color },
                emphasis: { itemStyle: { color } }
            };
        });

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'item',
                confine: true,
                appendToBody: false,
                ...getEChartsTooltipTheme(widgetBox),
                position: (point: number[], _params: any, _dom: any, _rect: any, size: any) => [
                    point[0] - size.contentSize[0] / 2,
                    point[1] + 8
                ],
                formatter: (params: any) => {
                    const symbol = params?.data?.assetSymbol ?? '';
                    return `
                    <div style="font-size:${fontSize}; line-height:1.35; text-align:center;">
                        ${this.escapeHtml(String(symbol))}
                    </div>
                `;
                }
            },
            grid: { left: 16, right: 24, top: 16, bottom: 28, containLabel: true },
            xAxis: {
                type: 'value',
                min: 0,
                max: scaleMaxTrades,
                splitNumber: 8,
                axisLabel: {
                    color: textColor,
                    fontSize: labelFontSize,
                    margin: 10
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: splitLineColor,
                        type: 'dotted'
                    }
                },
                axisLine: { show: false },
                axisTick: { show: false }
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: scaleMaxAmount,
                splitNumber: 7,
                axisLabel: {
                    color: textColor,
                    fontSize: labelFontSize,
                    margin: 10,
                    formatter: (value: number) => (value === 0 ? '' : this.formatBillions(value))
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: splitLineColor,
                        type: 'dotted'
                    }
                },
                axisLine: { show: false },
                axisTick: { show: false }
            },
            series: [
                {
                    id: BUBBLE_SERIES_ID,
                    type: 'scatter',
                    data: scatterData,
                    z: 2,
                    emphasis: { scale: 1 }
                },
                {
                    id: HOVER_DOT_SERIES_ID,
                    type: 'scatter',
                    silent: true,
                    symbolSize: 10,
                    itemStyle: { color: '#FFFFFF' },
                    data: [],
                    z: 6,
                    animation: false
                }
            ],
            animation: true,
            animationDuration: 900,
            animationEasing: 'cubicOut'
        };

        const chart = echarts.init(container, null, { renderer: 'canvas' });
        chart.setOption(option, true);
        chart.resize({ width: container.clientWidth || undefined, height: chartHeight });
        this.currentChart = chart;

        const setHoverDot = (point: number[] | null) => {
            chart.setOption({
                series: [{ id: HOVER_DOT_SERIES_ID, data: point ? [point] : [] }]
            });
        };

        chart.on('mouseover', { seriesId: BUBBLE_SERIES_ID }, (params: any) => {
            const value = params?.value as number[] | undefined;
            if (value) setHoverDot([value[0], value[1]]);
        });
        chart.on('mouseout', { seriesId: BUBBLE_SERIES_ID }, () => setHoverDot(null));
        chart.on('globalout', () => setHoverDot(null));

        requestAnimationFrame(() => {
            chart.resize({ width: container.clientWidth || undefined, height: chartHeight });
        });

        this.resizeHandler = () => {
            chart.resize({ width: container.clientWidth || undefined, height: chartHeight });
            this.updateWidgetLayout(chartHeight);
        };
        window.addEventListener('resize', this.resizeHandler);
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }

    public destroy(): void {
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler = null;
        }
        if (this.currentChart) {
            try { this.currentChart.dispose(); } catch { /* ignore */ }
            this.currentChart = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach(h => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global {
    interface Window {
        TraderverseCongressAllAssetsPopularity: typeof TraderverseCongressAllAssetsPopularity;
    }
}
if (typeof window !== 'undefined') {
    window.TraderverseCongressAllAssetsPopularity = TraderverseCongressAllAssetsPopularity;
}
